/*
 * Multi-conta — cofre de sessões.
 *
 * Roda na janela SHELL (que não tem o shim), então `localStorage` aqui é o real e
 * enxerga os namespaces `__s:<sid>:` de todas as contas. É assim que o shell lê o
 * e-mail/tenant de cada conta sem precisar que o iframe avise nada.
 *
 * Ver `public/session-shim.js` para o outro lado do mecanismo.
 */

export type Sessao = {
  sid: string;
  /** Rótulo editável mostrado na aba. Cai para o e-mail quando vazio. */
  rotulo?: string;
  criadaEm: number;
};

const CHAVE_SESSOES = "crm_sessions";
const CHAVE_ATIVA = "crm_sessao_ativa";
const PREFIXO = (sid: string) => `__s:${sid}:`;

/** Chaves que, presentes no namespace, indicam que a conta concluiu o login. */
const CHAVE_LOGADO = "id_cliente";

function lerJson<T>(chave: string, padrao: T): T {
  try {
    const cru = window.localStorage.getItem(chave);
    if (!cru) return padrao;
    const v = JSON.parse(cru);
    return v ?? padrao;
  } catch {
    return padrao;
  }
}

function gravarJson(chave: string, valor: unknown) {
  try {
    window.localStorage.setItem(chave, JSON.stringify(valor));
  } catch (e) {
    console.error("[multi-conta] falha ao gravar", chave, e);
  }
}

function novoSid(): string {
  // Curto e sem caracteres que atrapalhem em URL/window.name.
  const aleatorio = Math.random().toString(36).slice(2, 8);
  return `${Date.now().toString(36)}${aleatorio}`;
}

export function listarSessoes(): Sessao[] {
  const lista = lerJson<Sessao[]>(CHAVE_SESSOES, []);
  return Array.isArray(lista) ? lista.filter((s) => s && s.sid) : [];
}

function gravarSessoes(lista: Sessao[]) {
  gravarJson(CHAVE_SESSOES, lista);
}

export function criarSessao(rotulo?: string): Sessao {
  const nova: Sessao = { sid: novoSid(), rotulo, criadaEm: Date.now() };
  gravarSessoes([...listarSessoes(), nova]);
  return nova;
}

/**
 * Remove a conta e TODO o namespace dela do localStorage. Não toca nas outras.
 */
export function removerSessao(sid: string) {
  const restantes = listarSessoes().filter((s) => s.sid !== sid);
  gravarSessoes(restantes);

  const pref = PREFIXO(sid);
  const paraApagar: string[] = [];
  for (let i = 0; i < window.localStorage.length; i++) {
    const k = window.localStorage.key(i);
    if (k && k.startsWith(pref)) paraApagar.push(k);
  }
  paraApagar.forEach((k) => window.localStorage.removeItem(k));

  if (lerSessaoAtiva() === sid) {
    gravarSessaoAtiva(restantes[0]?.sid ?? null);
  }
}

export function renomearSessao(sid: string, rotulo: string) {
  gravarSessoes(
    listarSessoes().map((s) => (s.sid === sid ? { ...s, rotulo } : s))
  );
}

export function lerSessaoAtiva(): string | null {
  try {
    return window.localStorage.getItem(CHAVE_ATIVA);
  } catch {
    return null;
  }
}

export function gravarSessaoAtiva(sid: string | null) {
  try {
    if (sid) window.localStorage.setItem(CHAVE_ATIVA, sid);
    else window.localStorage.removeItem(CHAVE_ATIVA);
  } catch { /* storage indisponível */ }
}

/** Lê uma chave do namespace de uma conta (visão do shell). */
export function lerDaSessao(sid: string, chave: string): string | null {
  try {
    return window.localStorage.getItem(PREFIXO(sid) + chave);
  } catch {
    return null;
  }
}

export type ResumoSessao = {
  sid: string;
  rotulo: string;
  email: string | null;
  idCliente: string | null;
  idGrupo: string | null;
  adminMaster: boolean;
  logada: boolean;
};

/**
 * Junta o cadastro da aba com o que a conta gravou no próprio namespace.
 * É o que alimenta a barra de abas.
 */
export function resumirSessao(s: Sessao): ResumoSessao {
  const email = lerDaSessao(s.sid, "user_email");
  const idCliente = lerDaSessao(s.sid, CHAVE_LOGADO);
  return {
    sid: s.sid,
    rotulo: s.rotulo || email || "Nova conta",
    email,
    idCliente,
    idGrupo: lerDaSessao(s.sid, "id_grupo"),
    adminMaster: lerDaSessao(s.sid, "isAdminMaster") === "true",
    logada: !!idCliente,
  };
}

export function listarResumos(): ResumoSessao[] {
  return listarSessoes().map(resumirSessao);
}

/**
 * Migra a sessão que já está aberta no modo antigo (chaves na raiz do localStorage)
 * para uma conta do multi-conta, para que ligar o recurso não deslogue ninguém.
 * Copia as chaves de sessão para o namespace e devolve a conta criada — ou null se
 * não havia sessão na raiz.
 */
export function migrarSessaoLegada(): Sessao | null {
  const jaTem = listarSessoes().length > 0;
  if (jaTem) return null;

  const idCliente = window.localStorage.getItem(CHAVE_LOGADO);
  if (!idCliente) return null;

  // Tudo que não é preferência visual/branding pertence à sessão.
  const GLOBAIS = new Set([
    "rtl", "menuOpened", "dataWidth", "dataTopbarBg", "dataTopBar", "dataTheme",
    "dataSidebarBg", "dataSidebar", "dataLoader", "dataLayout", "dataColor",
    "dataCard", "dataTopbarAll", "__brand_cache_v1", CHAVE_SESSOES, CHAVE_ATIVA,
  ]);

  const nova = criarSessao(window.localStorage.getItem("user_email") || undefined);
  const pref = PREFIXO(nova.sid);

  const chaves: string[] = [];
  for (let i = 0; i < window.localStorage.length; i++) {
    const k = window.localStorage.key(i);
    if (k && !GLOBAIS.has(k) && !k.startsWith("__s:")) chaves.push(k);
  }
  chaves.forEach((k) => {
    const v = window.localStorage.getItem(k);
    if (v !== null) window.localStorage.setItem(pref + k, v);
  });

  gravarSessaoAtiva(nova.sid);
  return nova;
}

/** URL que o iframe da conta carrega. */
export function urlDaSessao(sid: string, rota = "/chat"): string {
  const base = rota.startsWith("/") ? rota : `/${rota}`;
  return `${base}?__sid=${encodeURIComponent(sid)}`;
}
